import { useNavigate } from '@tanstack/react-router';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { useCommiteeSlugNavigate } from '../utils/router';
import { Button } from './Button';

type ErrorPageProps = {
  title?: string;
  message: string;
  buttonText?: string;
  additionalInfo?: string;
};

export function ErrorPage({
  title = 'Une erreur est survenue',
  message,
  buttonText,
  additionalInfo,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const navigateWithCommiteeSlug = useCommiteeSlugNavigate();
  const { session } = useAuth();
  const commiteeSlug = session?.user?.commiteeSlug;

  const handleGoBack = () => {
    if (commiteeSlug) {
      // The session's slug, not the URL's: on an error page the URL's may be the invalid one.
      navigateWithCommiteeSlug({
        to: '/$commiteeSlug',
        params: { commiteeSlug },
      });
    } else {
      navigate({ to: '/gestion/general' });
    }
  };

  const defaultButtonText = commiteeSlug
    ? 'Retour à mon espace'
    : session
      ? 'Retour à la gestion'
      : 'Se connecter';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <ShieldAlert className="h-6 w-6 text-red-600" aria-label="Icône d'avertissement" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">{title}</h3>
          <p className="mt-2 text-sm text-gray-500">{message}</p>
          {additionalInfo && <p className="mt-2 text-sm text-gray-600">{additionalInfo}</p>}
          {commiteeSlug && (
            <p className="mt-2 text-sm text-gray-600">
              Vous êtes assigné à : <span className="font-semibold">{commiteeSlug}</span>
            </p>
          )}
          <div className="mt-6">
            <Button
              type="button"
              onClick={handleGoBack}
              variant="color"
              color="purple"
              className="w-full"
            >
              {buttonText || defaultButtonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
